import { ArrowRight, BookOpen, Check, ChevronDown, MicOff, Plus, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { href, navigate } from '../app/router';
import { Illustration } from '../components/Illustration';
import { WeekDots } from '../components/WeekDots';
import { course, getLesson, reviewCourse, trackFor, trackLessons, unitOf } from '../content';
import type { Level, Track, Unit } from '../content/types';
import { hasWork, planDay, REVIEW_MINUTES } from '../lib/plan';
import { candoState, lessonStatus, missionToAsk, nextCheck } from '../lib/progress';
import { dayKey } from '../lib/day';
import { InlineGreek } from '../components/InlineGreek';
import { toast } from '../components/Toast';
import { whenLabel } from '../lib/day';
import { yourHabit } from '../lib/habit';
import { useNow } from '../lib/useNow';
import { player } from '../audio/player';
import { nextReviews } from '../lib/upcoming';
import type { Persisted } from '../state/model';
import { answerMission, learnEarly, setQuiet, startSession, useAppState, usePersistent } from '../state/store';
import { lockOf, lockText, minutes, upNext } from '../stories';

/** Greek greetings by time of day: real phrases, from the first screen. */
export function greeting(now: Date): { greek: string; english: string } {
  const h = now.getHours();
  if (h >= 4 && h < 12) return { greek: 'Καλημέρα', english: 'Good morning' };
  if (h >= 12 && h < 17) return { greek: 'Γεια σου', english: 'Hello' };
  return { greek: 'Καλησπέρα', english: 'Good evening' };
}

function lowerFirst(s: string) {
  return s.charAt(0).toLowerCase() + s.slice(1);
}

const LEVEL_NAME: Record<Level, string> = { A1: 'Beginner', A2: 'Elementary', B1: 'Intermediate', B2: 'Upper intermediate' };

export function Today() {
  const state = useAppState();
  const persistent = usePersistent();
  const [busy, setBusy] = useState(false);
  useNow();
  const now = new Date();
  const plan = planDay(state, reviewCourse, now);
  const hello = greeting(now);
  const track = trackFor(state.profile.goal);
  const session = state.session;
  const resumable = Boolean(session && session.day === plan.day && !session.completedAt && session.quiet === plan.quiet);
  const started = resumable && session!.index > 0;
  const left = resumable ? session!.steps.length - session!.index : 0;
  const doneToday = state.profile.practiceDays.includes(plan.day);
  // An unfinished session counts as work even when the fresh plan has nothing left (e.g. only a repeat).
  const work = hasWork(plan) || (started && left > 0);
  const upcoming = nextReviews(state, now);
  const lessonId = plan.newItems[0]?.lesson ?? plan.sceneLessons[0];
  const lesson = lessonId ? getLesson(lessonId) : null;
  const place = lesson ? unitOf(track, lesson.id) : null;
  const reviewMinutes = Math.max(1, Math.round(plan.due.length * REVIEW_MINUTES));
  const pathDone = trackLessons(track).every((l) => state.profile.lessonsDone.includes(l.id));
  const quietWaiting = plan.quiet ? plan.waiting : 0;

  const start = async (reviewsOnly = false) => {
    if (busy) return;
    // This tap lets iOS play the session's audio later without a tap of its own.
    player.prime();
    setBusy(true);
    const s = await startSession(new Date(), { reviewsOnly });
    setBusy(false);
    if (s) navigate('/session');
  };

  const early = async () => {
    player.prime();
    setBusy(true);
    const s = await learnEarly(new Date());
    setBusy(false);
    if (s) navigate('/session');
  };

  const title = work
    ? started
      ? 'Almost there'
      : lesson && plan.newItems.length > 0
        ? lesson.title
        : plan.due.length > 0
          ? 'Reviews'
          : lesson
            ? `${lesson.title}: role-play`
            : 'Practice'
    : doneToday
      ? 'All done for today'
      : 'Nothing due right now';
  // Numbered along the whole path, as in the lesson itself, the phrasebook and the stories.
  const where = place ? ` · ${place.unit.title}, lesson ${trackLessons(track).findIndex((l) => l.id === lesson!.id) + 1}` : '';

  return (
    <main className="page today" id="main">
      <header className="today-head">
        <div className="stack" style={{ gap: 2 }}>
          <h1 className="title">
            <span lang="el">{hello.greek}</span>
          </h1>
          <p className="small muted">
            {hello.english}
            {state.profile.habit ? ` · your plan: ${yourHabit(state.profile.habit)}` : ''}
          </p>
        </div>
        <button type="button" className="track-chip" onClick={() => document.getElementById('path-h')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
          {track.label}
          <span className="sr-only">: show your path</span>
        </button>
      </header>

      {!persistent && (
        <div className="banner" role="note">
          <Sparkles size={18} aria-hidden="true" />
          <p>This browser isn't keeping data for this site (private browsing?), so your progress will be lost when you close it.</p>
        </div>
      )}

      <section className="panel today-card" aria-labelledby="today-h">
        <Illustration id={lesson?.id ?? track.cover} size="scene" priority decorative className="today-cover" />
        <div className="stack" style={{ gap: 4 }}>
          <p className="eyebrow" id="today-h">
            Today{work ? where : ''}
          </p>
          <h2 className="today-title">{title}</h2>
          {work && lesson && plan.newItems.length > 0 && !started && <p className="small muted">
              You'll learn to <InlineGreek text={lowerFirst(lesson.cando)} />
            </p>}
        </div>

        {work ? (
          <>
            <p className="today-facts">
              {[
                started && `${left} ${left === 1 ? 'step' : 'steps'} to go`,
                !started && plan.newItems.length > 0 && `${plan.newItems.length} new ${plan.newItems.length === 1 ? 'phrase' : 'phrases'}${plan.quiet ? ' by ear' : ''}`,
                !started && plan.due.length > 0 && `${plan.due.length} ${plan.due.length === 1 ? 'review' : 'reviews'}`,
                !started && plan.sceneLessons.length > 0 && plan.newItems.length === 0 && `role-play: ${getLesson(plan.sceneLessons[0]).title}`,
                `about ${plan.minutes} ${plan.minutes === 1 ? 'minute' : 'minutes'}`,
              ]
                .filter(Boolean)
                .join(' · ')
                .replace(/^./, (c) => c.toUpperCase())}
            </p>
            <button type="button" className="btn btn-primary btn-lg btn-block" onClick={() => start()} disabled={busy}>
              {started ? 'Continue' : doneToday ? 'Practise again' : 'Start'} <ArrowRight size={20} aria-hidden="true" />
            </button>
            {!started && plan.due.length > 0 && (plan.newItems.length > 0 || plan.sceneLessons.length > 0) && (
              <button type="button" className="btn btn-ghost btn-block" onClick={() => start(true)} disabled={busy}>
                Only reviews · about {reviewMinutes} {reviewMinutes === 1 ? 'minute' : 'minutes'}
              </button>
            )}
          </>
        ) : (
          <div className="stack">
            <p className="muted">
              {upcoming
                ? `Next: ${upcoming.count} ${upcoming.count === 1 ? 'review' : 'reviews'} ${upcoming.when}.`
                : plan.braked
                  ? 'New phrases are paused while reviews catch up.'
                  : 'You are all caught up.'}
              {plan.limitReached && !doneToday && ' Today’s new phrases are done.'}
            </p>
            {pathDone && <p className="small muted">You've done every lesson written so far. New units are on the way; your reviews carry on.</p>}
            {plan.limitReached && !plan.braked && (
              <button type="button" className="btn btn-secondary btn-block" onClick={early} disabled={busy}>
                <Plus size={18} aria-hidden="true" /> Learn the next lesson now
              </button>
            )}
          </div>
        )}

        {plan.braked && work && (
          <p className="small muted">You have a lot of reviews, so new phrases wait until they're under control.</p>
        )}

        <div className="setting today-quiet">
          <div className="setting-text">
            <span className="setting-label" id="quiet-l">
              <MicOff size={16} aria-hidden="true" style={{ verticalAlign: -2, marginRight: 6 }} />
              Can't speak right now?
            </span>
            <span className="setting-help">
              {plan.quiet
                ? quietWaiting > 0
                  ? `Listening only today: new phrases by ear, no speaking. ${quietWaiting} speaking ${quietWaiting === 1 ? 'card waits' : 'cards wait'} for later.`
                  : 'Listening only today: new phrases by ear, no speaking. Speaking waits for next time.'
                : 'Learn and review by ear only today.'}
            </span>
          </div>
          <button type="button" className="switch" role="switch" aria-checked={plan.quiet} aria-labelledby="quiet-l" onClick={() => setQuiet(!plan.quiet)} />
        </div>
      </section>

      <MissionCheck state={state} now={now} />

      <StoryNext state={state} />

      {state.profile.lessonsDone.length > 0 && <CanDos state={state} />}

      <section className="stack" aria-labelledby="week-h">
        <h2 className="section-title" id="week-h">
          This week
        </h2>
        <WeekDots practiceDays={state.profile.practiceDays} target={state.settings.weeklyTarget} now={now} />
      </section>

      <TrackPath track={track} state={state} />

      {!state.profile.readingGuideSeen && <ReadingPromo />}
    </main>
  );
}

/** The loop out to real life: the day after a lesson, did its "Try it for real" happen? Asked once. */
function MissionCheck({ state, now }: { state: Persisted; now: Date }) {
  const id = missionToAsk(state, dayKey(now));
  if (!id) return null;
  const lesson = getLesson(id);
  const answer = async (tried: boolean) => {
    await answerMission(id, tried);
    toast(tried ? 'Brilliant. That is what it is all for.' : 'No rush. It will keep for your next trip out.');
  };
  return (
    <section className="panel-quiet stack" aria-labelledby="mission-h">
      <div className="stack" style={{ gap: 4 }}>
        <p className="eyebrow">Try it for real · {lesson.title}</p>
        <h2 className="section-title" id="mission-h">
          Did you get a chance to try it?
        </h2>
        <p className="small muted">
          <InlineGreek text={lesson.mission} />
        </p>
      </div>
      <div className="row-wrap">
        <button type="button" className="btn btn-secondary" onClick={() => answer(true)}>
          Yes, I did
        </button>
        <button type="button" className="btn btn-ghost" onClick={() => answer(false)}>
          Not yet
        </button>
      </div>
    </section>
  );
}

/** What the learner can do now, honestly: settling in after the lesson, solid once reviews confirm it. */
function CanDos({ state }: { state: Persisted }) {
  const now = useNow();
  const done = course.lessons.filter((l) => state.profile.lessonsDone.includes(l.id));
  const solid = done.filter((l) => candoState(state, l, course) === 'solid').length;
  return (
    <section className="stack" aria-labelledby="cando-h">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h2 className="section-title" id="cando-h">
          What you can do
        </h2>
        <span className="small muted">
          {solid} solid · {done.length - solid} settling in
        </span>
      </div>
      <ul className="candos">
        {done.map((l) => {
          const state_ = candoState(state, l, course);
          return (
            <li key={l.id} className="cando" data-state={state_}>
              <Illustration id={l.id} size="thumb" decorative />
              <span className="cando-text">
                <span className="cando-title">{l.title}</span>
                <span className="cando-state">{state_ === 'solid' ? 'Solid' : 'Settling in'}</span>
                {state_ !== 'solid' && nextCheck(state, l) && <span className="cando-next">Next check {whenLabel(nextCheck(state, l)!, now)}</span>}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** The next story episode, for when there is time for something lighter. */
function StoryNext({ state }: { state: Persisted }) {
  const ep = upNext(state);
  if (!ep) return null;
  const started = Boolean(state.profile.stories?.[ep.id]);
  const lock = lockOf(state, ep);
  if (lock) {
    // Not ready yet: what is coming, and how many lessons to go.
    return (
      <div className="panel-quiet story-next" data-locked="true">
        <img src={ep.poster} alt="" width={960} height={540} loading="lazy" decoding="async" />
        <span className="stack" style={{ gap: 2, minWidth: 0 }}>
          <span className="eyebrow">
            {ep.number === 1 ? 'Coming up · a story in easy Greek' : `Coming up · episode ${ep.number}`}
          </span>
          <span className="story-next-title" lang="el">
            {ep.title}
          </span>
          <span className="small muted">{lockText(lock)}</span>
        </span>
      </div>
    );
  }
  return (
    <a href={href(`/story/${ep.id}`)} className="panel-quiet story-next">
      <img src={ep.poster} alt="" width={960} height={540} loading="lazy" decoding="async" />
      <span className="stack" style={{ gap: 2, minWidth: 0 }}>
        <span className="eyebrow">
          {started ? 'Story · keep watching' : ep.number === 1 ? 'New · a story in easy Greek' : 'Story · next episode'} · {minutes(ep.duration)}
        </span>
        <span className="story-next-title" lang="el">
          {ep.title}
        </span>
        <span className="small muted">
          Episode {ep.number}: {ep.titleEn}
        </span>
      </span>
    </a>
  );
}

function ReadingPromo() {
  return (
    <a href={href('/reading')} className="panel-quiet row" style={{ textDecoration: 'none', color: 'inherit' }}>
      <BookOpen size={22} aria-hidden="true" style={{ color: 'var(--ink-2)', flex: 'none' }} />
      <span className="stack" style={{ gap: 2 }}>
        <span className="section-title">New to the Greek alphabet?</span>
        <span className="small muted">A three-minute guide to reading the phrases, with the letters that trick English speakers.</span>
      </span>
    </a>
  );
}

/** The goal's path: units in order (by CEFR level for Expat), each with its lessons once written. */
function TrackPath({ track, state }: { track: Track; state: Persisted }) {
  const levels = track.id === 'expat' ? (['A1', 'A2', 'B1', 'B2'] as const) : [null];
  const written = track.units.filter((u) => u.lessons.length > 0);
  return (
    <section className="stack-lg track-path" aria-labelledby="path-h">
      <div className="stack" style={{ gap: 4 }}>
        <p className="eyebrow">Your path · {track.label}</p>
        <h2 className="title-md" id="path-h" style={{ scrollMarginTop: 24 }}>
          {track.name}
        </h2>
        <p className="small muted">
          {track.summary} {written.length < track.units.length && `${written.length} of ${track.units.length} units are ready; the rest are being written.`}
        </p>
      </div>
      {levels.map((level) => {
        const units = track.units.filter((u) => u.level === level);
        const list = (
          <ol className="units">
            {runsOf(units).map((run) =>
              run.length === 1 || run[0].lessons.length > 0 ? (
                run.map((unit) => <UnitCard key={unit.id} unit={unit} number={track.units.indexOf(unit) + 1} state={state} />)
              ) : (
                <SoonUnits key={run[0].id} units={run} first={track.units.indexOf(run[0]) + 1} />
              ),
            )}
          </ol>
        );
        if (!level) return <div key="all">{list}</div>;
        const open = units.some((u) => u.lessons.length > 0);
        return (
          <details key={level} className="level" open={open}>
            <summary>
              <span className="level-code">{level}</span>
              <span className="level-name">{LEVEL_NAME[level]}</span>
              <span className="level-count">{units.length} units</span>
              <ChevronDown size={18} aria-hidden="true" className="level-chevron" />
            </summary>
            {list}
          </details>
        );
      })}
    </section>
  );
}

/** Units in order, with consecutive unwritten ones grouped, so the path isn't a list of placeholders. */
function runsOf(units: Unit[]): Unit[][] {
  const runs: Unit[][] = [];
  for (const unit of units) {
    const last = runs[runs.length - 1];
    const soon = unit.lessons.length === 0;
    if (last && soon && last[0].lessons.length === 0) last.push(unit);
    else runs.push([unit]);
  }
  return runs;
}

/** Several units still being written, as one quiet row that opens to list them. */
function SoonUnits({ units, first }: { units: Unit[]; first: number }) {
  return (
    <li className="unit unit-soon" data-state="soon">
      <details>
        <summary className="unit-head">
          <span className="unit-num" aria-hidden="true">
            {first}–{first + units.length - 1}
          </span>
          <span className="stack" style={{ gap: 2, minWidth: 0 }}>
            <span className="unit-title">{units.length} more units being written</span>
            <span className="unit-meta">{units.map((u) => u.title).join(' · ')}</span>
          </span>
          <ChevronDown size={18} aria-hidden="true" className="level-chevron" />
        </summary>
        <ol className="soon-list" start={first}>
          {units.map((u) => (
            <li key={u.id}>
              {u.title} <span className="muted">· {u.plannedLessons} lessons</span>
            </li>
          ))}
        </ol>
      </details>
    </li>
  );
}

function UnitCard({ unit, number, state }: { unit: Unit; number: number; state: Persisted }) {
  const lessons = unit.lessons.map((id) => course.lessons.find((l) => l.id === id)!).filter(Boolean);
  const done = lessons.filter((l) => state.profile.lessonsDone.includes(l.id)).length;
  const ready = lessons.length > 0;
  const status = !ready ? 'soon' : done === unit.plannedLessons ? 'done' : 'open';
  return (
    <li className="unit" data-state={status}>
      <div className="unit-head">
        <span className="unit-num" aria-hidden="true">
          {status === 'done' ? <Check size={16} strokeWidth={3} /> : number}
        </span>
        <div className="stack" style={{ gap: 2, minWidth: 0 }}>
          <h3 className="unit-title">
            <span className="sr-only">Unit {number}: </span>
            <InlineGreek text={unit.title} />
          </h3>
          <p className="unit-meta">
            {ready
              ? `${done} of ${lessons.length} lessons done${unit.plannedLessons > lessons.length ? ` · ${unit.plannedLessons - lessons.length} more coming` : ''}`
              : `${unit.plannedLessons} lessons · being written`}
          </p>
        </div>
      </div>
      {ready && (
        <ol className="path">
          {lessons.map((l) => {
            const s = lessonStatus(state, l, course);
            return (
              <li key={l.id} className="path-item" data-status={s}>
                <span className="path-thumb" aria-hidden="true">
                  <Illustration id={l.id} size="thumb" decorative />
                  {s === 'done' && (
                    <span className="path-done">
                      <Check size={12} strokeWidth={3.5} />
                    </span>
                  )}
                </span>
                <div className="stack" style={{ gap: 2, minWidth: 0 }}>
                  <a href={href(`/phrases/${l.id}`)} className="path-title">
                    {l.title}
                    <span className="sr-only">{s === 'done' ? ', done' : s === 'current' ? ', up next' : ''}</span>
                  </a>
                  <span className="path-meta">{l.cando}</span>
                </div>
                {s === 'current' && <span className="chip chip-next">Up next</span>}
                {s === 'done' && (candoState(state, l, course) === 'solid' ? <span className="chip chip-solid">Solid</span> : <span className="chip">Settling in</span>)}
              </li>
            );
          })}
        </ol>
      )}
    </li>
  );
}
